import { useEffect, useMemo, useState } from 'react';

import { confirmLead, submitLead, trackFunnel } from './api.js';
import { LegalFooter } from './components/LegalFooter.js';
import { readPreview } from './preview.js';
import ConfirmEmail from './screens/ConfirmEmail.js';
import ContactForm, { type ContactFormValues } from './screens/ContactForm.js';
import Landing from './screens/Landing.js';
import QuestionScreen from './screens/QuestionScreen.js';
import Results from './screens/Results.js';
import ScoreReveal from './screens/ScoreReveal.js';
import { CONSENT, QUESTIONS } from '@shared/config.js';
import { scoreFromAnswers } from '@shared/scoring.js';
import type { ScanResult } from '@shared/types.js';
import type { ConsentRecord, OptionIndex } from '@shared/types.js';

/** currentStep, per the handoff state model: landing | q0..q11 | score | form | results */
type Step = 'landing' | 'question' | 'score' | 'form' | 'awaiting-confirmation' | 'results';

const NO_ANSWERS: (OptionIndex | null)[] = Array.from({ length: QUESTIONS.length }, () => null);

export default function App() {
  // Dev-only deep link (?preview=results&spl=3). Read once, before first paint,
  // so the requested screen renders immediately rather than flashing the
  // landing page. Compiles away entirely in a production build.
  const preview = useMemo(() => readPreview(), []);

  const [step, setStep] = useState<Step>(preview?.step ?? 'landing');
  const [questionIndex, setQuestionIndex] = useState(preview?.questionIndex ?? 0);
  const [answers, setAnswers] = useState<(OptionIndex | null)[]>(
    preview?.answers ?? NO_ANSWERS,
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [scorecardUrl, setScorecardUrl] = useState<string | null>(null);
  const [pending, setPending] = useState<{ email: string; expiresInHours: number } | null>(null);
  // Kept so the confirmation screen can re-send without asking again.
  const [lastValues, setLastValues] = useState<ContactFormValues | null>(null);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  // Set when the visitor arrives via the emailed link — possibly on another
  // device, so the result comes back from the server rather than local state.
  const [confirmed, setConfirmed] = useState<ScanResult | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // /confirm?token=... — redeem once on mount.
  useEffect(() => {
    if (!window.location.pathname.startsWith('/confirm')) return;
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      setConfirmError('This confirmation link is incomplete.');
      setStep('results');
      return;
    }
    setStep('results');
    confirmLead(token)
      .then((r) => {
        setConfirmed(r.result);
        setScorecardUrl(r.scorecardUrl);
        trackFunnel('scan_completed');
      })
      .catch((e: unknown) => setConfirmError(e instanceof Error ? e.message : 'Confirmation failed.'));
  }, []);

  const complete = answers.every((answer): answer is OptionIndex => answer !== null);

  // The client scores locally so the reveal is instant and works before any
  // network call. The server independently re-scores the same answers with the
  // same shared engine; the stored record and the PDF use the server's result.
  const result = useMemo(
    () => (complete ? scoreFromAnswers(answers as OptionIndex[]) : null),
    [answers, complete],
  );

  function recordAnswer(index: number, option: OptionIndex) {
    setAnswers((prev) => {
      const next = [...prev];
      next[index] = option;
      return next;
    });
  }

  function startScan() {
    trackFunnel('scan_started');
    setStep('question');
  }

  function advance() {
    if (questionIndex < QUESTIONS.length - 1) {
      setQuestionIndex(questionIndex + 1);
    } else {
      trackFunnel('scan_completed');
      setStep('score');
    }
  }

  function goBack() {
    if (questionIndex === 0) {
      setStep('landing');
      return;
    }
    setQuestionIndex(questionIndex - 1);
  }

  async function handleResend() {
    if (!lastValues) return;
    setResending(true);
    try {
      // The server recognises an unconfirmed lead for this address and issues a
      // fresh link against it instead of creating a second record.
      await submitLead(buildSubmission(lastValues));
      setResent(true);
    } catch {
      setResent(true); // never strand the visitor on an error here
    } finally {
      setResending(false);
    }
  }

  function buildSubmission(values: ContactFormValues) {
    const ts = new Date().toISOString();
    return {
      email: values.email.trim(),
      company: values.company.trim(),
      role: values.role as Exclude<ContactFormValues['role'], ''>,
      industry: values.industry,
      employees: values.employees as Exclude<ContactFormValues['employees'], ''>,
      consentProcessing: { given: true, ts, wordingVersion: CONSENT.processing.wordingVersion },
      consentMarketing: {
        given: values.marketingConsent,
        ts,
        wordingVersion: CONSENT.marketing.wordingVersion,
      },
      answers: answers as OptionIndex[],
    };
  }

  async function handleSubmit(values: ContactFormValues) {
    if (!complete) return;

    setLastValues(values);
    setSubmitting(true);
    setSubmitError(null);

    // Consent is stamped at the moment of submission, per purpose, together
    // with the exact wording version the visitor was shown.
    const ts = new Date().toISOString();
    const consentProcessing: ConsentRecord = {
      given: true, // implied by submitting; the statement sits above the button
      ts,
      wordingVersion: CONSENT.processing.wordingVersion,
    };
    const consentMarketing: ConsentRecord = {
      given: values.marketingConsent,
      ts,
      wordingVersion: CONSENT.marketing.wordingVersion,
    };

    try {
      const response = await submitLead({
        email: values.email.trim(),
        company: values.company.trim(),
        role: values.role as Exclude<ContactFormValues['role'], ''>,
        industry: values.industry,
        employees: values.employees as Exclude<ContactFormValues['employees'], ''>,
        consentProcessing,
        consentMarketing,
        answers: answers as OptionIndex[],
      });
      if (response.awaitingConfirmation) {
        // Double opt-in: the Scorecard is held back until the emailed link is
        // followed. The score itself was already given on the reveal screen and
        // is not retracted.
        setPending({
          email: response.emailSentTo ?? values.email.trim(),
          expiresInHours: response.expiresInHours,
        });
        setStep('awaiting-confirmation');
      } else {
        setScorecardUrl(response.scorecardUrl);
        setStep('results');
      }
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? `We couldn't prepare your Scorecard just now. ${error.message}`
          : "We couldn't prepare your Scorecard just now. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  // `import.meta.env.DEV` is replaced by the literal `false` at build time, so
  // this whole expression folds to null and the banner's markup is eliminated
  // from the production bundle rather than shipping as unreachable code.
  const banner = import.meta.env.DEV && preview ? (
    <div className="preview-banner">
      <b>Preview</b> · synthetic answers, no lead recorded. The Scorecard download needs a real
      submission. <code>?preview=results&amp;spl=0…4</code> ·{' '}
      <a href={window.location.pathname}>exit</a>
    </div>
  ) : null;

  const screen = renderStep();
  return banner ? (
    <>
      {banner}
      {screen}
    </>
  ) : (
    screen
  );

  function renderStep() {
  switch (step) {
    case 'landing':
      return <Landing onStart={startScan} />;

    case 'question':
      return (
        <QuestionScreen
          index={questionIndex}
          answers={answers}
          onAnswer={recordAnswer}
          onAdvance={advance}
          onBack={goBack}
        />
      );

    case 'score':
      return result ? (
        <ScoreReveal result={result} onContinue={() => setStep('form')} />
      ) : (
        <Landing onStart={startScan} />
      );

    case 'form':
      return result ? (
        <ContactForm
          result={result}
          submitting={submitting}
          submitError={submitError}
          onSubmit={handleSubmit}
        />
      ) : (
        <Landing onStart={startScan} />
      );

    case 'awaiting-confirmation':
      return pending ? (
        <ConfirmEmail
          email={pending.email}
          expiresInHours={pending.expiresInHours}
          onResend={handleResend}
          resending={resending}
          resent={resent}
        />
      ) : (
        <Landing onStart={startScan} />
      );

    case 'results': {
      // Either the local run, or a result returned by the confirmation link.
      const shown = confirmed ?? result;
      if (confirmError) {
        return (
          <div className="app">
            <main className="screen screen--center">
              <div className="confirm-wrap">
                <h1 className="confirm-h">This link didn't work</h1>
                <p className="confirm-lede">{confirmError}</p>
                <a className="btn btn-primary" href="/">
                  Run the scan again
                </a>
              </div>
            </main>
            <LegalFooter />
          </div>
        );
      }
      if (!shown) {
        return (
          <div className="app">
            <main className="screen screen--center">
              <div className="confirm-wrap">
                <div className="spinner" aria-hidden="true" />
                <p className="confirm-lede">Confirming your email…</p>
              </div>
            </main>
            <LegalFooter />
          </div>
        );
      }
      return <Results result={shown} scorecardUrl={scorecardUrl} />;
    }
  }
  }
}
